import { ReactNode, useState, useEffect, useRef, useMemo } from "react";
import { Users, FileText, UserCheck, Key, BarChart3, Menu, X, Calculator, Handshake, Phone, LogOut, BookOpen, Calendar, FileSignature, User, Upload, Trash2, HelpCircle, ExternalLink, GraduationCap, Car, ShoppingBag, Mail, Building2, FolderOpen, Percent, CheckSquare, AlertCircle, Settings, Bell, Search, Moon, Sun, ChevronLeft, ChevronRight, Receipt, LayoutGrid } from "lucide-react";
import { useQueryClient } from '@tanstack/react-query';
import { sectionPreloadMap, sectionDataPrefetchMap } from "@/lib/preloadSections";
import { prefetchReferenceData } from "@/lib/prefetchData";
import { useNetworkStatus, shouldPrefetch } from "@/hooks/useNetworkStatus";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { matchesSearchTerms } from "@/lib/searchUtils";
import { useAuth } from "@/hooks/useAuth";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useToast } from "@/hooks/use-toast";
import { useCachedPermissions } from "@/hooks/useCachedPermissions";
import { supabase } from "@/integrations/supabase/client";
import { ChatInterface } from "@/components/chat/ChatInterface";
import { useChat } from "@/hooks/useChat";
import { MultiActionButton } from "@/components/ui/multi-action-button";
import { useTheme } from "next-themes";
import { GlobalSearchBar } from "@/components/layout/GlobalSearchBar";
import { useGlobalFilter } from "@/contexts/GlobalFilterContext";
import { UploadStatusContent } from "@/components/ui/upload-status-content";
import { useUploadManager } from "@/contexts/UploadContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { SectionSearchProvider } from "@/contexts/SectionSearchContext";
import { NotificationBell } from "@/components/ui/notification-bell";
import { useIsMobile } from "@/hooks/use-mobile";
import { useWidgetLayoutContext } from "@/contexts/WidgetLayoutContext";
import { useConsultingMode } from "@/contexts/ConsultingModeContext";
import { ConsultingModeToggle } from "@/components/layout/ConsultingModeToggle";

// Widget Edit Menu Item Component - only for admins
const WidgetEditMenuItem = ({ isAdmin }: { isAdmin: boolean }) => {
  const { globalEditMode, setGlobalEditMode } = useWidgetLayoutContext();
  
  if (!isAdmin) return null;
  
  return (
    <DropdownMenuItem onClick={() => setGlobalEditMode(!globalEditMode)}>
      <LayoutGrid className="mr-2 h-4 w-4" />
      <span>{globalEditMode ? 'Widget-Bearbeitung beenden' : 'Widget-Bearbeitung'}</span>
    </DropdownMenuItem>
  );
};

interface CrmLayoutProps {
  children: ReactNode;
  currentSection: string;
  onSectionChange: (section: string, params?: any) => void;
}
const navigationItems = [{
  id: "dashboard",
  label: "Dashboard",
  icon: BarChart3,
  moduleKey: "dashboard"
}, {
  id: "email",
  label: "E-Mail",
  icon: Mail,
  moduleKey: "email"
}, {
  id: "meine-aufgaben",
  label: "Meine Aufgaben",
  icon: CheckSquare,
  moduleKey: "meine_aufgaben"
}, {
  id: "leads",
  label: "Leads",
  icon: UserCheck,
  moduleKey: "leads"
}, {
  id: "customers",
  label: "Kunden",
  icon: Users,
  moduleKey: "kunden"
}, {
  id: "contracts",
  label: "Verträge",
  icon: FileText,
  moduleKey: "vertraege"
}, {
  id: "produktanbieter",
  label: "Produktanbieter",
  icon: Building2,
  moduleKey: "produktanbieter"
}, {
  id: "downloadcenter",
  label: "Downloadcenter",
  icon: FolderOpen,
  moduleKey: "downloadcenter"
}, {
  id: "berichte",
  label: "Berichte",
  icon: BarChart3,
  moduleKey: "berichte"
}, {
  id: "vorlagen",
  label: "Vorlagen",
  icon: FileText,
  moduleKey: "vorlagen"
}, {
  id: "buchhaltung",
  label: "Buchhaltung",
  icon: Receipt,
  moduleKey: "buchhaltung"
}, {
  id: "vertriebspartner",
  label: "Vertriebspartner",
  icon: Handshake,
  moduleKey: "vertriebspartner"
}, {
  id: "elearning",
  label: "E-Learning",
  icon: GraduationCap,
  moduleKey: "elearning"
}, {
  id: "abrechnungen",
  label: "Abrechnungen",
  icon: Calculator,
  moduleKey: "abrechnungen"
}, {
  id: "hilfe-support",
  label: "Hilfe & Support",
  icon: HelpCircle,
  moduleKey: "hilfe_support"
}, {
  id: "einstellungen",
  label: "Einstellungen",
  icon: Settings,
  moduleKey: "einstellungen"
}];
export const CrmLayout = ({
  children,
  currentSection,
  onSectionChange
}: CrmLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [hoverExpandEnabled, setHoverExpandEnabled] = useState(true);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const mainContentRef = useRef<HTMLElement>(null);
  const { theme, setTheme } = useTheme();
  const { setGlobalFilterTerm } = useGlobalFilter();
  const [uploadStatusOpen, setUploadStatusOpen] = useState(false);
  const { uploads } = useUploadManager();
  const uploadCount = uploads.filter(u => u.status === 'uploading' || u.status === 'error').length;
  const queryClient = useQueryClient();
  const hasPreloadedRefDataRef = useRef(false);

  // Scroll to top when section changes
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [currentSection]);
  
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [currentPartner, setCurrentPartner] = useState<any>(null);
  const { logout, user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const { isOpen, setIsOpen } = useChat();
  
  // Use consolidated sidebar data with caching for optimistic UI
  const {
    data: sidebarData,
    isLoading: sidebarLoading,
    isCached
  } = useCachedPermissions(user?.id);
  
  // Extract data from consolidated response
  const isAdminDb = sidebarData?.is_admin || false;
  const accessibleModulesSet = useMemo(
    () => new Set(sidebarData?.accessible_modules || []),
    [sidebarData?.accessible_modules]
  );
  const partnerInfo = sidebarData?.partner_info;

  // Prefetch reference data once on mount
  useEffect(() => {
    if (!hasPreloadedRefDataRef.current && user?.id) {
      prefetchReferenceData(queryClient).catch((error) => {
        console.debug('Reference data prefetch failed:', error);
      });
      hasPreloadedRefDataRef.current = true;
    }
  }, [user?.id, queryClient]);

  // Load current user's partner data and sync with cached sidebar data
  useEffect(() => {
    if (partnerInfo) {
      setCurrentPartner(partnerInfo);
      setProfilePicture(partnerInfo.logo_url);
    } else if (user) {
      // Fallback: load from database if not in cached data
      const loadUserProfile = async () => {
        try {
          const { data: partner, error } = await supabase
            .from('vertriebspartner')
            .select('*')
            .eq('user_id', user.id)  // ID-first!
            .maybeSingle();          // Graceful handling of 0 rows
          if (error) {
            console.error('Error loading partner:', error);
            return;
          }
          if (partner) {
            setCurrentPartner(partner);
            setProfilePicture(partner.logo_url);
          }
        } catch (error) {
          console.error('Error loading profile:', error);
        }
      };
      loadUserProfile();
    }
  }, [user, partnerInfo]);

  const handleProfilePictureChange = (files: FileList | null) => {
    if (!files || !files[0]) return;
    const file = files[0];

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Ungültiger Dateityp",
        description: "Bitte wählen Sie eine Bild-Datei aus (JPG, PNG, etc.)",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Datei zu groß",
        description: "Das Profilbild darf maximal 5MB groß sein.",
        variant: "destructive"
      });
      return;
    }
    uploadProfilePicture(file);
  };

  const uploadProfilePicture = async (file: File) => {
    if (!user || !currentPartner) return;
    setUploadingProfile(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${currentPartner.id}-profile.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('partner-logos')
        .upload(fileName, file, { cacheControl: '3600', upsert: true });
      
      if (error) {
        console.error('Upload error:', error);
        toast({
          title: "Upload fehlgeschlagen",
          description: "Das Profilbild konnte nicht hochgeladen werden.",
          variant: "destructive"
        });
        return;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('partner-logos')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('vertriebspartner')
        .update({ logo_url: publicUrl })
        .eq('id', currentPartner.id);
      
      if (updateError) {
        console.error('Update error:', updateError);
        toast({
          title: "Speichern fehlgeschlagen",
          description: "Das Profilbild konnte nicht gespeichert werden.",
          variant: "destructive"
        });
        return;
      }

      setProfilePicture(publicUrl);
      setCurrentPartner(prev => ({ ...prev, logo_url: publicUrl }));
      setProfileDialogOpen(false);
      toast({
        title: "Profilbild aktualisiert",
        description: "Ihr Profilbild wurde erfolgreich geändert."
      });
    } catch (error) {
      console.error('Unexpected upload error:', error);
      toast({
        title: "Unerwarteter Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive"
      });
    } finally {
      setUploadingProfile(false);
    }
  };
  
  // Default modules always visible for optimistic rendering
  const defaultModules = new Set(['dashboard', 'meine-aufgaben', 'hilfe-support']);
  
  // Consulting mode config for filtering navigation
  const { isConsultingMode, config: consultingConfig } = useConsultingMode();
  
  // Map module_keys to navigation item IDs with optimistic display
  const accessibleNavigationItems = useMemo(() => {
    // Show default modules immediately even while loading
    if (authLoading || (sidebarLoading && !isCached)) {
      return new Set<string>(Array.from(defaultModules));
    }
    
    // Admin bypass: show all modules
    if (isAdminDb) {
      const allModules = new Set<string>(navigationItems.map(item => item.id));
      // Filter by consulting mode if active
      if (isConsultingMode && consultingConfig.hiddenModules.length > 0) {
        return new Set<string>(
          Array.from(allModules).filter(id => !consultingConfig.hiddenModules.includes(id))
        );
      }
      return allModules;
    }
    
    const accessible = new Set<string>();
    navigationItems.forEach(item => {
      
      // Vorlagen nur für Admins
      if (item.id === 'vorlagen') {
        if (isAdminDb) {
          accessible.add(item.id);
        }
        return;
      }
      
      if ((item as any).isExternal || !item.moduleKey) {
        accessible.add(item.id);
      } else if (accessibleModulesSet.has(item.moduleKey)) {
        accessible.add(item.id);
      }
    });
    
    // Filter by consulting mode if active
    if (isConsultingMode && consultingConfig.hiddenModules.length > 0) {
      return new Set<string>(
        Array.from(accessible).filter(id => !consultingConfig.hiddenModules.includes(id))
      );
    }
    
    return accessible;
  }, [accessibleModulesSet, isAdminDb, authLoading, sidebarLoading, isCached, isConsultingMode, consultingConfig.hiddenModules]);
  
  const permissionsLoaded = !authLoading && (!sidebarLoading || isCached);

  return (
    <SectionSearchProvider>
      <CrmLayoutContent
        currentSection={currentSection}
        onSectionChange={onSectionChange}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        sidebarHovered={sidebarHovered}
        setSidebarHovered={setSidebarHovered}
        hoverExpandEnabled={hoverExpandEnabled}
        setHoverExpandEnabled={setHoverExpandEnabled}
        profileDialogOpen={profileDialogOpen}
        setProfileDialogOpen={setProfileDialogOpen}
        mainContentRef={mainContentRef}
        profilePicture={profilePicture}
        uploadingProfile={uploadingProfile}
        currentPartner={currentPartner}
        user={user}
        isAdminDb={isAdminDb}
        logout={logout}
        handleProfilePictureChange={handleProfilePictureChange}
        accessibleNavigationItems={accessibleNavigationItems}
        permissionsLoaded={permissionsLoaded}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        uploadStatusOpen={uploadStatusOpen}
        setUploadStatusOpen={setUploadStatusOpen}
        uploadCount={uploadCount}
        queryClient={queryClient}
      >
        {children}
      </CrmLayoutContent>
    </SectionSearchProvider>
  );
};

interface CrmLayoutContentProps {
  children: ReactNode;
  currentSection: string;
  onSectionChange: (section: string, params?: any) => void;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  sidebarHovered: boolean;
  setSidebarHovered: (hovered: boolean) => void;
  hoverExpandEnabled: boolean;
  setHoverExpandEnabled: (enabled: boolean) => void;
  profileDialogOpen: boolean;
  setProfileDialogOpen: (open: boolean) => void;
  mainContentRef: React.RefObject<HTMLElement>;
  profilePicture: string | null;
  uploadingProfile: boolean;
  currentPartner: any;
  user: any;
  isAdminDb: boolean;
  logout: () => void;
  handleProfilePictureChange: (files: FileList | null) => void;
  accessibleNavigationItems: Set<string>;
  permissionsLoaded: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  uploadStatusOpen: boolean;
  setUploadStatusOpen: (open: boolean) => void;
  uploadCount: number;
  queryClient: any;
}

const CrmLayoutContent = ({
  children,
  currentSection,
  onSectionChange,
  sidebarOpen,
  setSidebarOpen,
  sidebarCollapsed,
  setSidebarCollapsed,
  sidebarHovered,
  setSidebarHovered,
  hoverExpandEnabled,
  setHoverExpandEnabled,
  profileDialogOpen,
  setProfileDialogOpen,
  mainContentRef,
  profilePicture,
  uploadingProfile,
  currentPartner,
  user,
  isAdminDb,
  logout,
  handleProfilePictureChange,
  accessibleNavigationItems,
  permissionsLoaded,
  isOpen,
  setIsOpen,
  uploadStatusOpen,
  setUploadStatusOpen,
  uploadCount,
  queryClient,
}: CrmLayoutContentProps) => {
  const { theme, setTheme } = useTheme();
  const { setGlobalFilterTerm } = useGlobalFilter();
  const networkStatus = useNetworkStatus();
  const { isConsultingMode, config: consultingConfig } = useConsultingMode();

  // Scroll to top when section changes
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [currentSection, mainContentRef]);

  const isExpanded = !sidebarCollapsed || (hoverExpandEnabled && sidebarHovered);

  return <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed inset-y-0 left-0 z-50 bg-background shadow-sidebar transition-[width,transform] duration-300 ease-in-out border-r border-border",
          "lg:relative lg:z-auto lg:translate-x-0 lg:h-screen lg:flex-shrink-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          isExpanded ? "w-64" : "w-16 lg:w-16"
        )}
        onMouseEnter={() => {
          if (!sidebarOpen && hoverExpandEnabled && typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) {
            setTimeout(() => setSidebarHovered(true), 400);
          }
        }}
        onMouseLeave={() => {
          setTimeout(() => setSidebarHovered(false), 200);
        }}
      >
          <div className="flex h-full flex-col">
          {/* Header */}
            <div className="flex h-16 items-center justify-between px-3 border-b border-border bg-primary relative">
            {/* Invisible spacer for symmetry */}
            <div className="h-10 w-10 lg:h-8 lg:w-8" />
            
              <div 
                className={cn(
                  "flex items-center justify-center",
                  isExpanded && "flex-col gap-1",
                  !isExpanded && "cursor-pointer lg:cursor-pointer"
                )}
              onClick={() => {
                if (!isExpanded && typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) {
                  setSidebarCollapsed(false);
                }
              }}
              title={!isExpanded ? "Sidebar ausklappen" : undefined}
            >
              <img 
                src="/lovable-uploads/17041aaf-232c-44ed-ae42-d61caaef22c9.png" 
                alt="VIVU Logo" 
                className="h-8 w-8 object-contain flex-shrink-0" 
              />
                {isExpanded && (
                  <h1 className="text-base font-bold text-primary-foreground whitespace-nowrap leading-none">VIVU CRM</h1>
                )}
            </div>
            
            <div className="h-10 w-10 lg:h-8 lg:w-8 flex items-center justify-center">
              <Button 
                variant="ghost" 
                size="sm" 
                  className="lg:hidden text-primary-foreground hover:bg-primary-foreground/10 h-10 w-10" 
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
              {isExpanded && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                    className="hidden lg:flex text-primary-foreground hover:bg-primary-foreground/10 h-8 w-8 p-0" 
                  onClick={() => {
                    setHoverExpandEnabled(false);
                    setSidebarCollapsed(true);
                    setSidebarHovered(false);
                  }}
                  title="Sidebar einklappen"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 min-h-0 px-2 py-4 space-y-1 overflow-y-auto">
            {!permissionsLoaded ? (
              // Show skeleton loader while fetching permissions
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2">
                  <div className="h-5 w-5 bg-muted animate-pulse rounded" />
                  {!sidebarCollapsed && (
                    <div className="h-4 flex-1 bg-muted animate-pulse rounded" />
                  )}
                </div>
              ))
            ) : accessibleNavigationItems.size === 0 ? (
              // Show message if no modules accessible
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                {!sidebarCollapsed && "Keine Module verfügbar"}
              </div>
            ) : (
              // Show filtered navigation items
              navigationItems
                .filter(item => accessibleNavigationItems.has(item.id))
                .map(item => {
            
            const Icon = item.icon;
            const isActive = currentSection === item.id;

            // External links get full URL, internal sections get hash
            const isExternal = (item as any).isExternal;
            const itemUrl = (item as any).url;
            const href = isExternal && itemUrl ? itemUrl : `#${item.id}`;
            const target = isExternal ? "_blank" : undefined;
            const rel = isExternal ? "noopener noreferrer" : undefined;
            return <a 
              key={item.id} 
              href={href} 
              target={target} 
              rel={rel} 
              onClick={e => {
                // For internal links, prevent default and use section change
                if (!isExternal) {
                  e.preventDefault();
                  onSectionChange(item.id);
                }
                setSidebarOpen(false);
              }}
              onMouseEnter={() => {
                // Skip prefetch on slow connections
                if (!shouldPrefetch(networkStatus)) return;
                
                // Prefetch dashboard data immediately
                if (item.id === 'dashboard' && user?.id) {
                  import('@/lib/prefetchData').then(({ prefetchDashboardData }) => {
                    prefetchDashboardData(queryClient, user.id).catch(() => {
                      // Silently fail
                    });
                  });
                }
                
                // Prefetch section bundle on hover for instant navigation
                if (!isExternal && sectionPreloadMap[item.id]) {
                  sectionPreloadMap[item.id]().catch(() => {
                    // Silently fail - section will load on click if prefetch fails
                  });
                }
                
                // Prefetch section data only on good connections (4G/WiFi)
                if (!isExternal && networkStatus.effectiveType === '4g' && sectionDataPrefetchMap[item.id] && user?.id) {
                  sectionDataPrefetchMap[item.id](queryClient, user.id, user.email || undefined);
                }
              }}
              className={cn(
                "group relative flex items-center px-3 py-3 text-left rounded-lg min-h-[44px]",
                "transition-[background-color,box-shadow,color] duration-200 ease-in-out",
                isActive 
                  ? "bg-primary text-primary-foreground shadow-sm" 
                  : "text-foreground hover:bg-muted",
                !isExpanded && "justify-center"
              )}
              title={!isExpanded ? item.label : undefined}
            >
              <Icon className={cn(
                "h-5 w-5 flex-shrink-0 transition-transform duration-200 ease-in-out",
                isExpanded && "mr-3"
              )} />
              {isExpanded && (
                <span className="font-medium text-sm whitespace-nowrap">{item.label}</span>
              )}
            </a>;
          })
            )}
          </nav>

          {/* Footer - Reserved for future use */}
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Header with search, notifications, theme toggle, and profile */}
        <header className="bg-background border-b border-border px-3 sm:px-4 py-3 flex items-center justify-between gap-1 sm:gap-2 shadow-sm min-h-[64px] sticky top-0 z-30 relative overflow-hidden">
          {/* Left side: Menu button and search */}
          <div className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSidebarOpen(true)} 
              className="text-foreground lg:hidden h-10 w-10 flex-shrink-0"
            >
              <Menu className="h-5 w-5" />
            </Button>
            
            {/* Global Search - only show if not hidden in consulting mode */}
            {(!isConsultingMode || !consultingConfig.hideGlobalSearch) && (
              <GlobalSearchBar
                onSectionChange={onSectionChange}
                onGlobalFilterChange={setGlobalFilterTerm}
              />
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Consulting Mode Toggle */}
            <ConsultingModeToggle />

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="h-10 w-10 flex-shrink-0"
            >
              {theme === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>

            {/* Notifications - only show if not hidden in consulting mode */}
            {(!isConsultingMode || !consultingConfig.hideNotifications) && (
              <NotificationBell onSectionChange={onSectionChange} />
            )}

            {/* Profile Dropdown */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-10 w-10 rounded-full p-0">
                  <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-border bg-muted flex items-center justify-center">
                    {profilePicture ? (
                      <img src={profilePicture} alt="Profilbild" className="w-full h-full object-cover" />
                    ) : (
                      <User className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => {
                  onSectionChange('einstellungen', { view: 'profile' });
                }}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Mein Profil</span>
                </DropdownMenuItem>
                <WidgetEditMenuItem isAdmin={isAdminDb} />
                {accessibleNavigationItems.has('einstellungen') && (
                  <DropdownMenuItem onClick={() => onSectionChange('einstellungen')}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Einstellungen</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Abmelden</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

      {/* Page content - main container scrolls vertically like Dashboard */}
      <main ref={mainContentRef} className="flex-1 px-2 py-4 sm:p-4 md:p-6 lg:p-8 max-w-full min-h-0 overflow-y-auto flex flex-col">
        {children}
      </main>

      {/* Multi-Action Button (Chat + Upload Hub) */}
      <MultiActionButton
        onChatClick={() => setIsOpen(true)}
        onUploadClick={() => setUploadStatusOpen(true)}
        hasNewMessages={false}
        uploadCount={uploadCount}
      />

      {/* Chat Interface */}
      <ChatInterface isOpen={isOpen} onClose={() => setIsOpen(false)} />

      {/* Upload Status Sheet */}
      <Sheet open={uploadStatusOpen} onOpenChange={setUploadStatusOpen}>
        <SheetContent>
          <UploadStatusContent />
        </SheetContent>
      </Sheet>

      {/* Profile Picture Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Profilbild ändern</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Current Profile Picture */}
            {profilePicture && <div className="flex items-center justify-center">
                <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-border bg-muted">
                  <img src={profilePicture} alt="Aktuelles Profilbild" className="w-full h-full object-cover" />
                </div>
              </div>}
            
            {/* Upload Section */}
            <div className="space-y-2">
              <Label htmlFor="profile-upload">Neues Profilbild hochladen</Label>
              <div>
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                  <input id="profile-upload" type="file" accept="image/*" onChange={e => handleProfilePictureChange(e.target.files)} className="hidden" disabled={uploadingProfile} />
                  <label htmlFor="profile-upload" className="cursor-pointer flex flex-col items-center space-y-2">
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <div className="text-sm text-muted-foreground">
                      {uploadingProfile ? <span className="text-primary font-medium">Wird hochgeladen...</span> : <>
                          <span className="font-medium text-primary">Profilbild hochladen</span>
                          <span className="block">oder hierher ziehen und ablegen</span>
                          <span className="block text-xs">JPG, PNG, max. 5MB</span>
                        </>}
                    </div>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setProfileDialogOpen(false)} disabled={uploadingProfile}>
                Abbrechen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>;
};